import React from 'react';
import Navbar from '../navbar/navbar.jsx';
import Footer from '../footer/footer.jsx';
import AnimatedSection from '../components/AnimatedSection.jsx';
import { translations } from './translations.js';

const Proyectos = ({ language, setLanguage, isDarkMode, setIsDarkMode }) => {
  const t = translations[language];

  return (
    <div 
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-stone-200 text-gray-900'
      }`}
      style={{ fontFamily: "Raleway, sans-serif" }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <Navbar 
        language={language} 
        setLanguage={setLanguage}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}      />
        {/* Header Section */}
      <AnimatedSection animationType="fadeInUp">
        <section className="pt-32 pb-16 px-12">
          <div className="max-w-6xl mx-auto text-center">
            <h1 
              className={`text-6xl lg:text-7xl font-bold mb-6 transition-colors duration-300 ${
                isDarkMode ? 'text-red-400' : 'text-red-900'
              }`}
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              {t.title}
            </h1>
            <p className={`text-xl mb-8 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t.subtitle}
            </p>
            <p className={`text-lg max-w-3xl mx-auto transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t.description}
            </p>
          </div>
        </section>
      </AnimatedSection>      {/* Projects Section */}
      <section className="py-3 px-12">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-20">
            {t.projects.map((project, index) => (
              <AnimatedSection 
                key={project.id}
                animationType={index % 2 === 0 ? 'fadeInLeft' : 'fadeInRight'}
                delay={index * 200}
              >
                <div 
                  className={`flex flex-col lg:flex-row gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >                {/* Image */}
                <div className="lg:w-1/2">
                  <div className={`relative overflow-hidden rounded-2xl shadow-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                  }`}>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-80 object-cover object-left-top hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2 space-y-6">
                  <div>
                    <h2 
                      className={`text-3xl lg:text-4xl font-light mb-4 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-800'
                      }`}
                      style={{ fontFamily: "Space Grotesk, sans-serif" }}
                    >
                      {project.title}
                    </h2>
                    <div className={`space-y-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <p className="text-sm font-medium">{project.date}</p>
                      <p className="text-sm">{project.status}</p>
                    </div>
                  </div>                  <p className={`leading-relaxed text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {project.description.split('**').map((part, index) => 
                      index % 2 === 0 ? part : <strong key={index}>{part}</strong>
                    )}
                  </p>

                <div className="flex gap-4 flex-wrap">
                    {project.hasNews ? (
                      <a 
                        href={project.newsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform ${
                          isDarkMode 
                            ? 'bg-red-600 hover:bg-red-500 text-white' 
                            : 'bg-red-900 hover:bg-red-800 text-white'
                        }`}
                      >
                        {t.viewNews} →
                      </a>
                    ) : project.hasProject ? (
                      // Special handling for certain projects:
                      // - If primaryRepoButton is true (ToDo project), make the filled red button point to repository and show "Ver Repositorio" text.
                      // - If project has a projectLink and is the Kepler project (id 4), label the filled button as "dataset" and point to the dataset.
                      project.primaryRepoButton ? (
                        <a
                          href={project.repositoryLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform ${
                            isDarkMode 
                              ? 'bg-red-600 hover:bg-red-500 text-white' 
                              : 'bg-red-900 hover:bg-red-800 text-white'
                          }`}
                        >
                          {t.viewRepository} →
                        </a>
                      ) : project.id === 4 && project.projectLink ? (
                        <>
                          <a 
                            href={project.projectLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform ${
                              isDarkMode 
                                ? 'bg-red-600 hover:bg-red-500 text-white' 
                                : 'bg-red-900 hover:bg-red-800 text-white'
                            }`}
                          >
                            dataset →
                          </a>

                          <a 
                            href={project.repositoryLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform border-2 ${
                              isDarkMode 
                                ? 'border-red-600 text-red-400 hover:bg-red-600 hover:text-white' 
                                : 'border-red-900 text-red-900 hover:bg-red-900 hover:text-white'
                            }`}
                          >
                            {t.viewRepository} →
                          </a>
                        </>
                      ) : (
                        <>
                          <a 
                            href={project.projectLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform ${
                              isDarkMode 
                                ? 'bg-red-600 hover:bg-red-500 text-white' 
                                : 'bg-red-900 hover:bg-red-800 text-white'
                            }`}
                          >
                            {t.viewProject} →
                          </a>

                          <a 
                            href={project.repositoryLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform border-2 ${
                              isDarkMode 
                                ? 'border-red-600 text-red-400 hover:bg-red-600 hover:text-white' 
                                : 'border-red-900 text-red-900 hover:bg-red-900 hover:text-white'
                            }`}
                          >
                            {t.viewRepository} →
                          </a>
                        </>
                      )
                    ) : (
                      <a 
                        href={project.repositoryLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 uppercase text-sm tracking-wide hover:scale-105 transform ${
                          isDarkMode 
                            ? 'bg-red-600 hover:bg-red-500 text-white' 
                            : 'bg-red-900 hover:bg-red-800 text-white'
                        }`}
                      >
                        {t.viewRepository} →
                      </a>
                    )}                  </div>
                </div>
              </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <Footer language={language} isDarkMode={isDarkMode} />
    </div>
  );
};

export default Proyectos;